import { IExportKeyMember } from '~/client/src/shared/map/exportLabelTypes'
import {
  ILabelParts,
  labelPartsText,
} from '~/client/src/shared/map/labelLineCap'
import Workflow from '~/client/src/shared/models/Workflow'

import { stripUnsupportedGlyphs } from '../../utils/textUtils'
import {
  DEFAULT_COMPANY_LOGO_IMAGE_ID,
  getCompanyLogoImageId,
} from '../SitemapHelpers/utils/GLCompanyLogoImageRegistrar'
import type { IWorkflowPinLabelPoint } from './GLWorkflowPinLabelLayer'
import {
  groupWorkflowsByCoord,
  IWorkflowPinCoordOverride,
} from './workflowPinPoints'

export const EMPTY_PIN_LABELS: IWorkflowPinLabelPoint[] = []

// Pin-label sort keys carry recency as -startDate, so they span roughly -2e12..0. A selected
// label is lifted into its own band beneath that whole range, which keeps it placed first
// however the dates fall.
const SELECTED_LABEL_SORT_BAND = 1e13

const NBSP = '\u00a0'

export interface IWorkflowPinLabelContext {
  coordOverrides: Map<string, IWorkflowPinCoordOverride>
  anchorCircleShownIds: Set<string>
  getMonthAndDay?: (startDate: number) => string
  getCompany: (
    companyId: string,
  ) => { avatarUrl?: string; logoUrl?: string } | null | undefined
  isSelected: (group: Workflow[]) => boolean
}

// No year: the filter bar above the map already states the active range, and the whole clause is
// only 0.8% of the block. Zero-padding stripped the same way getSmartTimestamp does it — "Aug 7",
// not "Aug 07".
function workflowPinLabelParts(
  permit: Workflow,
  ctx: Pick<IWorkflowPinLabelContext, 'getMonthAndDay'>,
): ILabelParts {
  const date = permit.startDate
    ? (ctx.getMonthAndDay?.(permit.startDate).replace(/\b0(\d)/g, '$1') ?? '')
    : ''
  // NBSP binds month and day, so the day number is never orphaned on a line of its own at 1.15
  // leading. The clause itself can still break: the space before it, and the middle dot, which
  // mapbox treats as a break point.
  const dateClause = date ? `\u00b7${NBSP}${date.replace(/ /g, NBSP)}` : ''
  return {
    prefix: stripUnsupportedGlyphs(permit.code ?? ''),
    separator: ': ',
    title: stripUnsupportedGlyphs(permit.title ?? ''),
    suffix: dateClause ? ` ${dateClause}` : '',
  }
}

const recencyOf = (permit: Workflow): number =>
  permit.startDate ? -new Date(permit.startDate).getTime() : 0

// Pin labels (STX1-686 D5) ignore the "association labels" toggle — it governs
// MARKUP labels only; avatarUrl→logoUrl is the app-wide logo fallback order.
// One label per coord group of SIZE 1 — 2+ workflows collapse into a count bubble
// instead, so labels never pile up at a shared anchor coord.
export function buildWorkflowPinLabelPoints(
  pinnedWorkflows: Workflow[],
  ctx: IWorkflowPinLabelContext,
): {
  points: IWorkflowPinLabelPoint[]
  logoUrls: Map<string, string>
} {
  const logoUrls = new Map<string, string>()
  const { workflowsByCoordKey, coordByKey } = groupWorkflowsByCoord(
    pinnedWorkflows,
    ctx.coordOverrides,
  )
  const points: IWorkflowPinLabelPoint[] = []
  for (const [key, group] of workflowsByCoordKey) {
    if (group.length !== 1) {
      continue
    }
    const permit = group[0]
    const coord = coordByKey.get(key)
    const companyId = permit.companyIds?.[0]
    const company = companyId ? ctx.getCompany(companyId) : null
    const logoUrl = company?.avatarUrl || company?.logoUrl
    const logoImageId = logoUrl
      ? getCompanyLogoImageId(companyId)
      : DEFAULT_COMPANY_LOGO_IMAGE_ID
    if (logoUrl) {
      logoUrls.set(logoImageId, logoUrl)
    }
    const isSelected = ctx.isSelected(group)
    points.push({
      id: permit.id,
      coordKey: key,
      latitude: coord.latitude,
      longitude: coord.longitude,
      labelParts: workflowPinLabelParts(permit, ctx),
      logoImageId,
      // Riding a marker is what makes this label's position contested — the marker's circle
      // and name label already stand where it would go — so it goes on the layer that places
      // dynamically instead of dropping straight down into them.
      anchored:
        coord.anchorItemId != null &&
        ctx.anchorCircleShownIds.has(coord.anchorItemId),
      isSelected,
      // Most-recent workflow wins when labels declutter (lower = placed first), but a
      // selected one outranks the whole resting set whatever its date — hence a band
      // below the recency range rather than a magic number inside it.
      sortKey: (isSelected ? -SELECTED_LABEL_SORT_BAND : 0) + recencyOf(permit),
    })
  }
  return points.length
    ? { points, logoUrls }
    : { points: EMPTY_PIN_LABELS, logoUrls }
}

// A stack's Workflows worded as their labels, for the export's label key to list. Apart from the
// labels, so the live map never words Workflows it only counts.
export function buildWorkflowPinStackMembers(
  pinnedWorkflows: Workflow[],
  ctx: Pick<IWorkflowPinLabelContext, 'coordOverrides' | 'getMonthAndDay'>,
): Map<string, IExportKeyMember[]> {
  const members = new Map<string, IExportKeyMember[]>()
  const { workflowsByCoordKey } = groupWorkflowsByCoord(
    pinnedWorkflows,
    ctx.coordOverrides,
  )
  for (const [key, group] of workflowsByCoordKey) {
    if (group.length > 1) {
      members.set(
        key,
        group.map(permit => ({
          text: labelPartsText(workflowPinLabelParts(permit, ctx)),
          sortKey: recencyOf(permit),
        })),
      )
    }
  }
  return members
}
